import React from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { AuthScreen } from "../features/auth/AuthScreen";
import { ConfirmSignInScreen } from "../features/auth/ConfirmSignInScreen";
import { SupportAdvertiserList } from "../features/support/SupportAdvertiserList";
import { ExportTemplatesPage } from "../features/support/ExportTemplatesPage";
import { QuickBooksExceptionsPage } from "../features/support/QuickBooksExceptionsPage";
import { QuickBooksInvoiceExportPage } from "../features/support/QuickBooksInvoiceExportPage";
import { QuickBooksLayout } from "../features/support/QuickBooksLayout";
import { QuickBooksReconciliationPage } from "../features/support/QuickBooksReconciliationPage";
import { QuickBooksConnectionTab } from "../features/support/QuickBooksConnectionTab";
import { ScreenEnter } from "../components/motion/ScreenEnter";
import { MainLayout } from "./MainLayout";
import { DashboardScreen } from "../features/dashboard/DashboardScreen";
import { CreativeScreen } from "../features/creative/CreativeScreen";
import { ReportsScreen } from "../features/reports/ReportsScreen";
import { PaymentsScreen } from "../features/payments/PaymentsScreen";
import {
  clearShopifyInstallNonce,
  adoptInstallNonceCookie,
  clearShopifyPendingConfirm,
  persistShopifyInstallNonce,
  persistShopifyPendingConfirm,
  readShopifyInstallNonce,
  useShopifyPendingConfirm,
} from "../features/integrations/useShopifyPendingConfirm";
import { generateInstallNonce, startShopifyInstallFromQuery } from "../services/shopifyService";
import { useAmazonLoginCapture, useAmazonPendingAuthorize } from "../features/integrations/useAmazonPendingAuthorize";
import { AMAZON_LOGIN_PARAMS } from "../lib/amazonPendingAuthorize";
import {
  ADVERTISER_ID_PARAM,
  APP_OWN_PARAMS,
  GA_PARAM,
  INTEGRATION_RESULT_PARAMS,
  LOGOUT_PARAM,
  SALES_INTEGRATION_SOURCES,
} from "../lib/shopifyInstallRedirect";

// Wrapper that injects ga + integration props into MainLayout so the nav
// knows to open integrations.
function MainLayoutWithGa({ gaOpenIntegrations, gaErrorFromUrl, integrationResult, clearIntegrationResult }) {
  return (
    <MainLayout
      gaOpenIntegrations={gaOpenIntegrations}
      gaErrorFromUrl={gaErrorFromUrl}
      integrationResult={integrationResult}
      clearIntegrationResult={clearIntegrationResult}
    />
  );
}

// Removes one or more query params in a single history replace — a single
// name still goes through here (via a stable one-element array; see
// LOGOUT_PARAM etc. below) rather than a separate single-param hook, so there
// is one strip implementation instead of two. Several names at once (e.g.
// ?integration=&status=&reason=) disappear together rather than one at a
// time, which would otherwise fire this component's effects once per param
// and add extra history noise. Names must be passed as a stable reference —
// a module-level array, not an inline literal — so this hook's useCallback
// doesn't get a new dependency identity on every render.
function useStripParams(names) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  return React.useCallback(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    for (const name of names) {
      if (next.has(name)) {
        next.delete(name);
        changed = true;
      }
    }
    if (!changed) return;
    const search = next.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : "" },
      { replace: true },
    );
  }, [navigate, location.pathname, searchParams, names]);
}

// LOGOUT_PARAM etc. and APP_OWN_PARAMS (this app's own one-shot params, never
// swept into a captured Shopify install query) are stable module-level arrays
// shared with the install middleware (lib/shopifyInstallRedirect.js), so
// useStripParams's useCallback deps don't churn and there is one list.

function LoadingShim() {
  return (
    <div style={{ minHeight:"100vh", background:"#FFFFFF", fontFamily:"'DM Sans','Helvetica Neue',sans-serif", color:"#151515", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <span style={{ color:"#626262" }}>Loading…</span>
    </div>
  );
}

function RequireAuth() {
  const { session, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!session) return <Navigate to="/login" replace state={{ from: location }} />;
  return <Outlet />;
}

function RequireSupport() {
  const { isSupportUser } = useAuth();
  if (!isSupportUser) return <Navigate to="/" replace />;
  return <Outlet />;
}

// Support users only see advertiser screens while masquerading as one.
function needsMasquerade({ isSupportUser, masqueradeAdvertiserId }) {
  return isSupportUser && masqueradeAdvertiserId == null;
}

function RequireAdvertiserView() {
  if (needsMasquerade(useAuth())) return <Navigate to="/support" replace />;
  return <Outlet />;
}

export function AuthGate() {
  const auth = useAuth();
  const { session, loading, signOut, isSupportUser, setMasquerade } = auth;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const stripLogout = useStripParams(LOGOUT_PARAM);
  const stripAdvId = useStripParams(ADVERTISER_ID_PARAM);
  const stripGa = useStripParams(GA_PARAM);
  const stripIntegrationParams = useStripParams(INTEGRATION_RESULT_PARAMS);
  const stripAmazonLoginParams = useStripParams(AMAZON_LOGIN_PARAMS);

  // ?ga= one-shot: signals the integrations panel to open after OAuth callback.
  const [gaOpenIntegrations, setGaOpenIntegrations] = React.useState(false);
  const [gaErrorFromUrl, setGaErrorFromUrl] = React.useState(false);
  // ?integration=&status=&reason= one-shot: the generic sales-integration
  // return path (Shopify today, Amazon later). Independent of ?ga=, which
  // stays GA4-specific.
  const [integrationResult, setIntegrationResult] = React.useState(null);

  // -1. Adopt the install nonce cookie middleware.js sets when it answers
  // Shopify's install request with a server-side 302. Declared before every
  // other effect so the localStorage entry exists before anything reads it
  // (the post-OAuth ?integration=…&origin=install handling and
  // useShopifyPendingConfirm both wait for a session, which resolves later).
  React.useEffect(() => {
    adoptInstallNonceCookie();
  }, []);

  // 0. Shopify install capture (DEV-2332, OAuth-first — replaces DEV-2299's
  // resume-after-login path). Shopify's legacy install flow loads the App
  // URL with `?shop=…&hmac=…&host=…&timestamp=…` (an embedded install may
  // also carry extras like `locale`, `session`, `id_token`). Shopify's App
  // Store checklist requires reaching the OAuth grant screen IMMEDIATELY,
  // before any other step — including the Audiohook login. So this now
  // calls shopify-oauth's `begin-install` action right here, with NO session
  // required, instead of stashing the query and waiting for a sign-in first.
  //
  // Captures every param present EXCEPT this app's own one-shot params
  // (APP_OWN_PARAMS) — not a fixed five-name list — and strips exactly the
  // captured names from the URL immediately, before the (async)
  // startShopifyInstallFromQuery call resolves. Shopify extras like
  // `locale`, `session`, `id_token` are swept up and removed too, while this
  // app's own params (e.g. `?ga=`) are left alone for their own handlers
  // below. `beginInstallStartedRef` guards against firing it twice for the
  // same captured query (e.g. React StrictMode's double effect invocation).
  //
  // The outcome mapping itself (what a redirect/already_connected/error
  // result means) lives in shopifyService.js's startShopifyInstallFromQuery
  // — this effect only captures the query, strips the URL and acts on the
  // three possible `kind`s, staying a thin capture-and-delegate effect like
  // the others below.
  const beginInstallStartedRef = React.useRef(null);
  React.useEffect(() => {
    const shop = searchParams.get("shop");
    const hmac = searchParams.get("hmac");
    if (!shop || !hmac) return;

    const captured = new URLSearchParams();
    const next = new URLSearchParams(searchParams);
    let changed = false;
    for (const [key, value] of searchParams.entries()) {
      if (APP_OWN_PARAMS.includes(key)) continue;
      captured.append(key, value);
      if (next.has(key)) {
        next.delete(key);
        changed = true;
      }
    }
    const installQuery = captured.toString();

    // Strip on every pass (idempotent — a no-op once already gone). Other
    // one-shot effects below (e.g. ?ga=) strip their own param off the same
    // original searchParams snapshot in the same commit; each effect only
    // sees convergence once searchParams updates and effects re-run, so
    // guarding the strip itself (rather than just the network call below)
    // would leave a param behind forever whenever two one-shot params arrive
    // together.
    if (changed) {
      const search = next.toString();
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : "" },
        { replace: true },
      );
    }

    // The install-start call itself must fire at most once per captured
    // query, even though the effect above may re-run on every convergence
    // pass.
    if (beginInstallStartedRef.current === installQuery) return;
    beginInstallStartedRef.current = installQuery;

    // DEV-2332 code-review round: generate the browser-binding nonce and
    // persist it BEFORE calling begin-install (not after it resolves) — the
    // tab may navigate straight to Shopify's authorize screen the instant
    // the response arrives, and the nonce must already be in localStorage by
    // then for the later confirm step to find it. `shop` comes from the
    // install query itself (Shopify-signed, not merchant-entered) so the
    // eventual confirm prompt's shop name never has to trust the callback
    // redirect's `connect_shop` param.
    //
    // Second review round (item 6): persistShopifyInstallNonce now reports
    // whether the write actually landed (it reads the value back and
    // compares). If storage is blocked (private mode, a full quota, a
    // blocked site-data setting), begin-install is never called at all — the
    // eventual confirm could never succeed without this entry, so starting
    // OAuth would just strand the merchant on Shopify's side with no way
    // back. Surface a specific, actionable note instead.
    const nonce = generateInstallNonce();
    const nonceStored = persistShopifyInstallNonce({ nonce, shop });
    if (!nonceStored) {
      setIntegrationResult({ source: "shopify", status: "error", reason: "install_storage_unavailable", confirmToken: null });
      return;
    }

    (async () => {
      const outcome = await startShopifyInstallFromQuery(installQuery, nonce);
      if (outcome.kind === "redirect") {
        window.location.assign(outcome.url);
        return;
      }
      // No more `already_connected` kind (DEV-2332 code-review round removed
      // begin-install's short-circuit) — every non-redirect outcome here is
      // an invalid/tampered/stale link or a server error. Second review
      // round (item 3): clear the nonce we just persisted — nothing will
      // ever consume it since this install attempt never reached Shopify.
      clearShopifyInstallNonce();
      setIntegrationResult({ source: "shopify", status: "error", reason: "install_link_invalid", confirmToken: null });
    })();
  }, [searchParams, navigate, location.pathname]);

  // 0b. Amazon Login URI capture (DEV-2216). Like the Shopify capture above
  // this runs before every route guard, signed in or not: the params are
  // persisted and stripped, and useAmazonPendingAuthorize (below) surfaces
  // them as an explicit prompt once signed in.
  useAmazonLoginCapture({ setIntegrationResult, stripLoginParams: stripAmazonLoginParams });

  // 1. ?logout=1 one-shot — preserves prior behavior.
  React.useEffect(() => {
    if (searchParams.get("logout") === "1") {
      signOut();
      stripLogout();
    }
  }, [searchParams, signOut, stripLogout]);

  // 2. ?advertiser_id=N deep link — only meaningful for support users.
  // Guarded on !loading && session so the param stays in the URL until the
  // session resolves; we never silently no-op on a transient loading state.
  React.useEffect(() => {
    if (loading || !session) return;
    const raw = searchParams.get("advertiser_id");
    if (raw == null) return;
    if (!isSupportUser) {
      stripAdvId();
      return;
    }
    const trimmed = raw.trim();
    const n = parseInt(trimmed, 10);
    if (Number.isFinite(n) && String(n) === trimmed) {
      setMasquerade(n, "");
    }
    stripAdvId();
  }, [loading, session, isSupportUser, searchParams, setMasquerade, stripAdvId]);

  // 3. ?ga=connected / ?ga=error — one-shot post-OAuth redirect handling.
  // Strip the param immediately and signal the integrations panel.
  React.useEffect(() => {
    const gaParam = searchParams.get("ga");
    if (gaParam == null) return;
    if (gaParam === "connected") {
      setGaOpenIntegrations(true);
      setGaErrorFromUrl(false);
    } else if (gaParam === "error") {
      setGaErrorFromUrl(true);
      setGaOpenIntegrations(true);
    }
    stripGa();
  }, [searchParams, stripGa]);

  // 4. ?integration=<source>&status=connected|pending|error&reason=<code>
  // &confirm=<token> — one-shot post-redirect handling for any sales
  // integration OAuth flow (DEV-2211). status=pending&confirm=<token> is the
  // confirm-in-app step (code-review MEDIUM #2): the callback staged the
  // connection but did not write it, and SalesIntegrationsSection must
  // present this token back to the source's confirmConnect before it's real.
  React.useEffect(() => {
    const integration = searchParams.get("integration");
    // Only sales sources are handled here. Another integration's result (e.g.
    // QuickBooks, read by QuickBooksConnectionCard on /support/quickbooks) is left for
    // its own screen: this effect runs while the session is still loading, so
    // stripping it here would remove the params before that screen mounts.
    if (!SALES_INTEGRATION_SOURCES.includes(integration)) return;
    const rawStatus = searchParams.get("status");
    const status = rawStatus === "connected" || rawStatus === "pending" ? rawStatus : "error";
    const reason = searchParams.get("reason");
    const confirmToken = searchParams.get("confirm");
    const origin = searchParams.get("origin");

    // Second review round (item 3): install-origin is decided ONLY from the
    // `origin=install` URL param now — never from the mere presence of a
    // nonce entry in localStorage. The first code-review round used "a
    // nonce entry exists" as a second signal, reasoning a stripped `origin`
    // param was the same attack minus one tell; that heuristic let an
    // unrelated leftover nonce entry (a previous, abandoned install attempt)
    // route an ordinary bound-flow pending result into the prompt path
    // instead. The server independently requires a matching install_nonce
    // for any null-owner row (088's confirm_pending_connection) regardless
    // of what the SPA believes here, so trusting the URL param alone is
    // sufficient: an in-app (no `origin`) pending result always takes the
    // automatic bound confirm path below, exactly as DEV-2211 shipped it.
    if (integration === "shopify" && status === "pending" && origin === "install" && confirmToken) {
      const nonceEntry = readShopifyInstallNonce();
      if (!nonceEntry) {
        // origin=install arrived but no matching nonce entry survives
        // (missing, expired, or a shared-machine leftover). Second review
        // round (item 6): show an error note instead of silently dropping
        // it — the merchant otherwise sees the app just... do nothing.
        clearShopifyPendingConfirm();
        setIntegrationResult({ source: "shopify", status: "error", reason: "install_confirm_missing", confirmToken: null });
        stripIntegrationParams();
        return;
      }
      // The prompt's shop name comes from the nonce entry (this browser's
      // own record of what it started installing), never the URL's
      // `connect_shop` param, which could be spoofed independently of the
      // token.
      persistShopifyPendingConfirm({ shop: nonceEntry.shop, confirmToken });
      stripIntegrationParams();
      return;
    }

    // Second review round (item 3): an OAuth error/denied result for
    // shopify (whatever its origin) means whatever install nonce this
    // browser may have generated for that attempt is now moot — nothing
    // will ever consume it, so it must not linger and be offered again on a
    // future, unrelated attempt.
    if (integration === "shopify" && status === "error") {
      clearShopifyInstallNonce();
    }

    setIntegrationResult({ source: integration, status, reason, confirmToken });
    stripIntegrationParams();
  }, [searchParams, stripIntegrationParams]);

  // Given to SalesIntegrationsSection (via MainLayout/UserMenu) so the result
  // note is not permanent: dismissed explicitly, superseded by a fresh
  // connect/disconnect on the same source, or dropped on an advertiser
  // switch. useCallback keeps this reference stable across renders, since
  // SalesIntegrationsSection depends on it in an effect's dependency array.
  const clearIntegrationResult = React.useCallback(() => {
    setIntegrationResult(null);
  }, []);

  // DEV-2332: surfaces a persisted install-origin pending confirm (see effect
  // 4 above) once signed in with an advertiser resolved — reuses
  // setIntegrationResult as the same one-shot "open the Integrations panel"
  // mechanism the OAuth callback path uses, with a distinct status
  // ("pending_confirm_prompt") that SalesIntegrationsSection renders as an
  // explicit prompt rather than an automatic confirm.
  useShopifyPendingConfirm({ openIntegrationsResult: setIntegrationResult });
  // DEV-2216: the same for a persisted Amazon Login URI request (effect 0b),
  // surfaced as status "authorize_prompt".
  useAmazonPendingAuthorize({ openIntegrationsResult: setIntegrationResult });

  if (loading) return <LoadingShim />;

  // Magic link landing (any path, GoTrue may fall back to the Site URL root).
  // Rendered even with a session: the link may be for another account. The
  // screen strips token_hash and type from the router URL once verified.
  if (searchParams.has("token_hash")) return <ConfirmSignInScreen />;

  // Hold the route tree while a ?advertiser_id deep link is present so
  // screens never mount with a stale effectiveAdvertiserId before the
  // masquerade effect above has a chance to run.
  if (session && searchParams.has("advertiser_id")) {
    return <LoadingShim />;
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          session ? (
            <Navigate
              to={needsMasquerade(auth) ? "/support" : "/"}
              replace
            />
          ) : (
            <AuthScreen />
          )
        }
      />

      <Route element={<RequireAuth />}>
        {/* Support-only branch */}
        <Route element={<RequireSupport />}>
          <Route path="/support" element={<ScreenEnter fadeOnly><SupportAdvertiserList /></ScreenEnter>} />
          <Route path="/support/export-templates" element={<ScreenEnter fadeOnly><ExportTemplatesPage /></ScreenEnter>} />
          <Route path="/support/quickbooks" element={<ScreenEnter fadeOnly><QuickBooksLayout /></ScreenEnter>}>
            <Route index element={<QuickBooksConnectionTab />} />
            <Route path="exceptions" element={<QuickBooksExceptionsPage />} />
            <Route path="invoice-export" element={<QuickBooksInvoiceExportPage />} />
            <Route path="reconciliation" element={<QuickBooksReconciliationPage />} />
          </Route>
          {/* The invoice export's first address (DEV-2600). */}
          <Route path="/support/billing-export" element={<Navigate to="/support/quickbooks/invoice-export" replace />} />
        </Route>

        {/* Main app branch — support users without an active masquerade are
            redirected to /support for every route nested here, so new
            advertiser screens are guarded by default. */}
        <Route element={<RequireAdvertiserView />}>
          <Route
            element={
              <MainLayoutWithGa
                gaOpenIntegrations={gaOpenIntegrations}
                gaErrorFromUrl={gaErrorFromUrl}
                integrationResult={integrationResult}
                clearIntegrationResult={clearIntegrationResult}
              />
            }
          >
            <Route index element={<DashboardScreen />} />
            <Route path="creative" element={<CreativeScreen />} />
            <Route path="reports" element={<ReportsScreen />} />
            <Route path="payments" element={<PaymentsScreen />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
